/**
 * EasySend/1Pays Webhook Schemas (Platcore Platform)
 *
 * Raw webhook payloads from EasySend and 1Pays providers.
 * Both providers use the same Platcore platform with identical payload format.
 *
 * Used by:
 *   - payment-service/src/webhooks/easysend.ts
 *   - payment-service/src/webhooks/onepays.ts
 *
 * Endpoints:
 *   POST /webhook/easysend
 *   POST /webhook/1pays
 *
 * Security:
 *   - x-api-key: Service account ID
 *   - x-api-signature: RSA-signed message digest
 *   - TODO: Implement signature verification (Phase 6)
 */
import { z } from 'zod';
// =============================================================================
// EASYSEND/1PAYS WEBHOOK BODY
// Source: payment-service/src/webhooks/easysend.ts:43-69
// =============================================================================
/**
 * Status object nested in payload
 */
export const easysendStatusSchema = z.object({
    name: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
}).passthrough();
/**
 * Metadata object nested in payload
 */
export const easysendMetadataSchema = z.object({
    clientId: z.string().optional(),
    paymentId: z.string().optional(),
    invoiceId: z.string().optional(),
}).passthrough();
/**
 * Currency object nested in payload
 */
export const easysendCurrencySchema = z.object({
    code: z.string().optional(),
}).passthrough();
/**
 * Fees object nested in payload
 */
export const easysendFeesSchema = z.object({
    total: z.number().optional(),
}).passthrough();
/**
 * EasySend/1Pays webhook payload
 *
 * Example:
 * {
 *   _id: "0a000000-a6d9-4fb9-0000-000000000000",
 *   orderId: "I124686676",
 *   merchantId: "...",
 *   projectId: "...",
 *   amount: 7000,
 *   status: {
 *     name: "completed",
 *     createdAt: "2024-02-05T09:19:14.963Z",
 *     updatedAt: "2024-02-05T09:19:14.963Z"
 *   },
 *   metadata: {
 *     clientId: "485904-005904",
 *     paymentId: "ZXC-51707124686"
 *   },
 *   ...
 * }
 */
export const easysendWebhookBodySchema = z.object({
    /** Platcore transaction UUID */
    _id: z.string().optional(),
    /** Order ID */
    orderId: z.string().optional(),
    /** Merchant identifier */
    merchantId: z.string().optional(),
    /** Project identifier */
    projectId: z.string().optional(),
    /** Amount in local currency */
    amount: z.number().optional(),
    /** Status object with name */
    status: easysendStatusSchema.optional(),
    /** Metadata with our internal IDs */
    metadata: easysendMetadataSchema.optional(),
    /** Source currency */
    currencyFrom: easysendCurrencySchema.optional(),
    /** Target currency */
    currencyTo: easysendCurrencySchema.optional(),
    /** Fees breakdown */
    fees: easysendFeesSchema.optional(),
    /** Whether dispute is open */
    disputeOpen: z.boolean().optional(),
}).passthrough();
export const onepaysWebhookBodySchema = easysendWebhookBodySchema;
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/**
 * Checks if required fields are present
 */
export function hasEasySendRequiredFields(body) {
    return !!(body._id && body.status?.name);
}
// Alias for 1Pays
export const hasOnePaysRequiredFields = hasEasySendRequiredFields;
// =============================================================================
// STATUS MAPPING
// =============================================================================
/**
 * Maps EasySend/1Pays raw status to normalized WebhookStatus
 * - completed → completed
 * - canceled/cancelled/refunded/expired → cancelled
 * - failed → failed
 * - queued/new/pending/paid → pending
 *
 * NOTE: "paid" is NOT terminal - waiting for trader confirmation
 */
export function mapEasySendStatus(statusName) {
    switch (statusName?.toLowerCase()) {
        case 'completed':
            return 'completed';
        case 'canceled':
        case 'cancelled':
            return 'cancelled';
        case 'refunded':
            return 'cancelled'; // Treat refunded as cancelled
        case 'expired':
            return 'cancelled';
        case 'failed':
            return 'failed';
        case 'queued':
        case 'new':
        case 'pending':
        case 'paid': // paid is NOT terminal - waiting for trader confirmation
            return 'pending';
        default:
            return 'pending';
    }
}
// Alias for 1Pays (same logic)
export const mapOnePaysStatus = mapEasySendStatus;
//# sourceMappingURL=easysend.js.map